// src/shared/components/ui/textarea.tsx

import { useEffect, useRef, type TextareaHTMLAttributes } from 'react'

import { cn } from '@/shared/utils/cn'

type TextareaSize = 'sm' | 'md' | 'lg'

const textareaSizeMap: Record<TextareaSize, string> = {
  sm: 'min-h-20 text-xs px-2.5 py-2',
  md: 'min-h-24 text-sm px-3 py-2.5',
  lg: 'min-h-28 text-sm px-3 py-3',
}

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  textareaSize?: TextareaSize
  invalid?: boolean
  autoResize?: boolean
}

export function Textarea({
  className,
  textareaSize = 'md',
  invalid,
  autoResize,
  value,
  onChange,
  ...props
}: TextareaProps) {
  const ref = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (!autoResize || !ref.current) return
    const el = ref.current
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [autoResize, value])

  return (
    <textarea
      aria-invalid={invalid || undefined}
      className={cn(
        'w-full rounded-md border bg-[var(--panel)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] transition focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
        textareaSizeMap[textareaSize],
        invalid
          ? 'border-[var(--color-danger)] focus-visible:ring-[var(--color-danger)]'
          : 'border-[var(--panel-border)] focus-visible:ring-[var(--color-brand)] focus-visible:border-[var(--color-brand)]',
        className,
      )}
      onChange={onChange}
      ref={ref}
      value={value}
      {...props}
    />
  )
}
